import { Col, Image, Row } from "react-bootstrap";
import { useState, useEffect, useRef } from 'react'
import Select from 'components/general/ViewportSelect';
import { useSelector } from 'react-redux';
import { notifyEditPermissionDenied } from 'utils/editPermissionToast';
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { isMemberEquivalent, isOfficer } from 'utils/acl'
import { getUserInitials, resolveAvatarSrc } from 'utils/avatar'

const getAvatarSrc = (avatar) => resolveAvatarSrc(avatar);

export default function Presenter({ item, isPlaying, setIsPlaying, handleStop, time, setTime, getMeeting, isLocked = false }) {
  const { user: authUser } = useSelector(x => x.auth)
  const url = process.env.REACT_APP_URL
  const meetingId = useParams().meetingId

  const dropdownRef = useRef(null); // Create a ref for the dropdown container

  const [showDropdown, setShowDropdown] = useState(false); // State to toggle dropdown visibility
  const [presenters, setPresenters] = useState({ officersAndMembers: [], guests: [] })
  const [selectedPresenterId, setSelectedPresenterId] = useState('')
  const canChoosePresenter = Boolean(authUser?.token && isOfficer(authUser?.user?.acl || authUser?.acl || '') && !isLocked);
  const assignedUserInitials = getUserInitials(item?.assignedUser, 'T')
  const initialAssignedAvatar = getAvatarSrc(item?.assignedUser?.avatarUrl || item?.assignedUser?.avatar)
  const [assignedAvatar, setAssignedAvatar] = useState(initialAssignedAvatar)

  useEffect(() => {
    let mounted = true
    const initial = getAvatarSrc(item?.assignedUser?.avatarUrl || item?.assignedUser?.avatar)
    setAssignedAvatar(initial)
    if (!initial) {
      const key = String(item?.assignedUser?.tmAuthUserId || item?.assignedUser?._id || '').trim()
      if (key) {
        (async () => {
          try {
            const resp = await fetch(`${url}/unified/${encodeURIComponent(key)}`)
            if (!mounted) return
            if (!resp.ok) return
            const payload = await resp.json().catch(() => null)
            const avatar = payload?.identity?.avatarUrl || payload?.identity?.avatar || null
            const src = avatar ? getAvatarSrc(avatar) : null
            if (src && mounted) setAssignedAvatar(src)
          } catch (_err) {
            // ignore
          }
        })()
      }
    }

    return () => { mounted = false }
    // eslint-disable-next-line
  }, [item?.assignedUser])

  useEffect(() => {
    const userId = item?.assignedUser?.id || item?.assignedUser?._id || item?.assignedUser?.tmAuthUserId || '';
    setSelectedPresenterId(userId);
  }, [item?.assignedUser]);

  const getPresenters = async () => {
    const response = await fetch(`${url}/users`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authUser.token}`
      }
    })
    if (response.status === 200) {
      const presenters = await response.json()
      // Group presenters by role
      const officersAndMembers = presenters.filter(p => isMemberEquivalent(p.acl)).sort((a, b) => a.firstName.localeCompare(b.firstName))
      const guests = presenters.filter(p => p.acl === 'Guest').sort((a, b) => a.firstName.localeCompare(b.firstName))
      setPresenters({ officersAndMembers, guests })
    }
    else {
      console.log(response)
    }
  }

  const presenterOptions = [
    {
      label: 'Members',
      options: presenters.officersAndMembers.map(presenter => ({ value: presenter._id, label: `${presenter.firstName} ${presenter.lastName}` }))
    },
    {
      label: 'Guests',
      options: presenters.guests.map(presenter => ({ value: presenter._id, label: `${presenter.firstName} ${presenter.lastName}` }))
    }
  ]

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
    if (!showDropdown) {
      getPresenters(); // Fetch users when the dropdown is opened
    }
  };

  const handlePresenterClick = () => {
    if (isLocked) return;
    if (!canChoosePresenter) {
      notifyEditPermissionDenied(authUser);
      return;
    }
    toggleDropdown();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        toggleDropdown(); // Close the dropdown if clicked outside
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line
  }, [showDropdown]); // Re-run the effect when showDropdown changes

  const handleUserChange = async (selectedOption) => {
    if (!canChoosePresenter) {
      notifyEditPermissionDenied(authUser);
      return;
    }
    let wasPlaying = isPlaying;
    let lastTime = time; // Save the current timer value
    // console.log(`LastTime: ${lastTime}, wasPlaying: ${wasPlaying}`)

    if (wasPlaying) {
      await handleStop();
    }
    try {
      if (!selectedOption || !selectedOption.value) {
        if (item.roleAssignmentId) {
          const deleteResponse = await fetch(`${url}/roleAssignment/${item.roleAssignmentId}`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authUser.token}`,
            },
          });
          if (deleteResponse.status !== 200) {
            throw new Error('Failed to remove presenter assignment');
          }
        }
        item.assignedUser = null;
        setSelectedPresenterId('');
        setShowDropdown(false);
      } else if (item.roleAssignmentId === undefined || item.roleAssignmentId === null) {
        const roleId = item.meetingRole?._id || item.meetingRole?.id || item.meetingRole;
        if (!roleId) throw new Error('Role assignment could not be created: meeting role is missing');
        // Role assignment does not exist, create it
        const createResponse = await fetch(`${url}/roleAssignment/${meetingId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authUser.token}`,
          },
          body: JSON.stringify({
            role: roleId,
            user: selectedOption.value,
          }),
        });
        // Check if the role assignment exists
        if (createResponse.status === 201 || createResponse.status === 200) {
          // Update the assigned user details in the item object
          const updatedUser = presenters.officersAndMembers
            .concat(presenters.guests)
            .find(user => user._id === selectedOption.value);

          if (updatedUser) {
            item.assignedUser = {
              id: updatedUser._id || null,
              avatar: updatedUser.avatar || null,
              firstName: updatedUser.firstName,
              lastName: updatedUser.lastName,
            };
          }
          setSelectedPresenterId(selectedOption.value);
          setShowDropdown(false); // Hide the dropdown after selection
        } else {
          throw new Error('Failed to create role assignment');
        }
      }
      else {
        const updateResponse = await fetch(`${url}/roleAssignment/${item.roleAssignmentId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authUser.token}`,
          },
          body: JSON.stringify({
            user: selectedOption.value,
          }),
        });

        if (updateResponse.status === 200) {
          // Update the assigned user details in the item object
          const updatedUser = presenters.officersAndMembers
            .concat(presenters.guests)
            .find(user => user._id === selectedOption.value);

          if (updatedUser) {
            item.assignedUser = {
              id: updatedUser._id || null,
              avatar: updatedUser.avatar || null,
              firstName: updatedUser.firstName,
              lastName: updatedUser.lastName,
            };
          }

          setSelectedPresenterId(selectedOption.value);
          setShowDropdown(false); // Hide the dropdown after selection
        } else {
          throw new Error('Failed to update user assignment');
        }
      }

    } catch (err) {
      toast.error('' + err, { position: 'bottom-left' });
    }
    await getMeeting()
    if (wasPlaying) {

      // If the new user has no timer, set it to lastTime
      if (!item.timer || item.timer.time === 0) {
        setTime(lastTime);
      } else {
        setTime(item.timer.time);
      }
      // setIsPlaying(false);
      setTimeout(() => setIsPlaying(true), 0);
    }
  };

  // Exact custom styles for the Select component as in AgendaItemEditModal.js
  const isDark = document.body.getAttribute('data-theme') === 'dark';
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: isDark ? '#23272b' : '#fff',
      color: isDark ? '#fff' : '#222',
      borderColor: state.isFocused ? (isDark ? '#FFD700' : '#004165') : (isDark ? '#444' : '#ccc'),
      boxShadow: state.isFocused ? `0 0 0 2px ${isDark ? '#FFD700' : '#004165'}` : 'none',
      minHeight: '44px',
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 99999,
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: isDark ? '#23272b' : '#fff',
      color: isDark ? '#fff' : '#222',
      zIndex: 99999,
    }),
    singleValue: (provided) => ({
      ...provided,
      color: isDark ? '#fff' : '#222',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? (isDark ? '#FFD700' : '#004165')
        : state.isFocused
          ? (isDark ? 'rgba(255,215,0,0.08)' : 'rgba(0,65,101,0.08)')
          : (isDark ? '#23272b' : '#fff'),
      color: state.isSelected
        ? (isDark ? '#23272b' : '#fff')
        : (isDark ? '#fff' : '#222'),
      fontWeight: state.isSelected ? 700 : 400,
    }),
    input: (provided) => ({
      ...provided,
      color: isDark ? '#fff' : '#222',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: isDark ? '#888' : '#888',
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: isDark ? '#FFD700' : '#004165',
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      backgroundColor: isDark ? '#FFD700' : '#004165',
    }),
  };

  return (
    <Col xs={12} lg={6}>
      <Row className='presenter'>
        <Col xs={12}>
          <Row className='avatar justify-content-center'>
            <Col>
              {assignedAvatar
                ? <Image src={assignedAvatar} style={{ maxHeight: '35vh', maxWidth: '35vw' }} />
                : (
                  <span
                    style={{
                      width: '35vw',
                      maxWidth: '35vh',
                      aspectRatio: '1 / 1',
                      borderRadius: '50%',
                      background: 'rgba(255,255,255,0.12)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: '3rem'
                    }}
                  >
                    <span className='meetings-mock-avatar-fallback'>{assignedUserInitials}</span>
                  </span>
                )}
            </Col>
          </Row>
        </Col>
        <Col>
          <Row>
            <Col>
              {showDropdown ? (
                <div ref={dropdownRef} className="mt-2">
                  <Select
                    autoFocus
                    defaultMenuIsOpen
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    menuPosition="fixed"
                    options={presenterOptions}
                    className='mb-3 formInput'
                    name='presenter'
                    value={presenterOptions.flatMap(group => group.options).find(option => option.value === selectedPresenterId) || null}
                    onChange={handleUserChange}
                    isClearable
                    isSearchable
                    styles={customStyles}
                    placeholder='Select a presenter'
                  />
                </div>
              ) : (
                <>
                  {canChoosePresenter ?
                    <h1 onClick={handlePresenterClick} style={{ cursor: 'pointer' }}>
                      {item?.assignedUser?.firstName ? item.assignedUser.firstName.toUpperCase() : 'TBA'}
                    </h1>
                    :
                    <h1 onClick={handlePresenterClick} style={{ cursor: isLocked ? 'default' : 'pointer' }}>
                      {item?.assignedUser?.firstName ? item.assignedUser.firstName.toUpperCase() : 'TBA'}
                    </h1>
                  }
                  {canChoosePresenter ? (
                    <h5
                      onClick={handlePresenterClick} style={{ cursor: 'pointer' }}
                    >
                      {item?.assignedUser?.lastName ? item.assignedUser.lastName.toUpperCase() : ''}
                    </h5>
                  ) : (
                    <h5 onClick={handlePresenterClick} style={{ cursor: isLocked ? 'default' : 'pointer' }}>
                      {item?.assignedUser?.lastName ? item.assignedUser.lastName.toUpperCase() : ''}
                    </h5>
                  )}
                </>
              )}
            </Col>
          </Row>
        </Col>
      </Row>
    </Col>
  );
}
